import type { ReactNode } from 'react'
import { api, stopViewAs, type Me } from '../api'
import { LangSwitch, useT } from '../lang'
import { Link, usePath } from '../router'

/* Four destinations, named for what they hold rather than for how the data
 * model is shaped.
 *
 * "Fleet" is gone: it is a word for a hundred units, it meant nothing with one,
 * and it sat next to a second item also called Hydrophones which was a
 * different list of hydrophones. The deployed units are what a person means by
 * "my hydrophones", so they get the name and the landing page; the sensor
 * inventory is really a record of calibration certificates, so it is called
 * that. */
/* Nouns for what the product does, not for the kit it does it with.
 *
 * This read Hydrophones / Sites / Calibration / Activity -- four equipment and
 * telemetry words, which is the vocabulary of every environmental monitoring
 * product there has ever been. The words that make this one distinct were one
 * level down, on the tabs: Sound, Listen, Recordings.
 *
 * So Listening is the fleet, Places are where it happens, Hydrophones is the
 * kit page that has earned the kit word, and Delivery is what arrived. Plain
 * words used consistently, rather than a coined vocabulary nobody can repeat
 * back. */
/* `staffOnly` marks the destinations that are about Phoniq's own operation
 * rather than about a customer's sea.
 *
 * Two of these were not merely uninteresting to a customer, they were broken:
 * Delivery reads /v1/audit and /v1/attribution-conflicts, Ocean impact reads
 * /v1/impact, and all three became require_internal when scoping landed. A
 * customer clicking them got an error box. A navigation item that cannot work
 * is worse than a missing one -- it teaches people the product is unreliable
 * at the exact moment they are deciding whether to trust it. */
interface NavEntry {
  /** Adressen posten peker på. For en kundepost er dette et HOPP: `/loggbok`
   *  sender deg videre til `/sites/{id}/loggbok` og står aldri i adressefeltet
   *  når siden er framme. */
  to: string
  label: string
  short: string
  staffOnly: boolean
  icon: ReactNode
  /** Siden under `/sites/{id}` som hoppet faktisk lander på, uten
   *  anleggsdelen -- `'/loggbok'`, eller `''` når produktet ER anleggssiden.
   *  Det er dette som gjør at menyen kan merke seg selv: uten det vet den bare
   *  om hoppet, og hoppet er borte i det øyeblikket siden vises. */
  under?: string
  /** Bare i skrivebordsmenyen, ikke i telefonens linje nederst. */
  railOnly?: boolean
  /** Utelatt betyr «alltid». Postene til Phoniq selv er allerede silt ut på
   *  `staffOnly` og trenger ingen. */
  can?: (me: Me) => boolean
}

const NAV: NavEntry[] = [
  { to: '/', label: 'Listening posts', short: 'Posts', staffOnly: true,
    icon: <><path d="M12 3v7" /><circle cx="12" cy="15" r="4" /><path d="M8.5 3h7" /></> },
  { to: '/sites', label: 'Places', short: 'Places', staffOnly: true,
    icon: <><path d="M12 21s7-5.3 7-11a7 7 0 1 0-14 0c0 5.7 7 11 7 11Z" /><circle cx="12" cy="10" r="2.5" /></> },
  /* The log, in the staff rail as well as the customer one.
   *
   * It is a customer product, and the rail is otherwise split cleanly between
   * what Phoniq operates and what a customer bought -- but the same argument
   * that put Listening in the customer rail applies here in reverse: a product
   * nobody at Phoniq can reach from their own session is one nobody can
   * support. The hop behind it lists whichever farms have it. */
  { to: '/logg', label: 'Log', short: 'Log', staffOnly: true,
    icon: <><circle cx="12" cy="12" r="9" /><path d="M12 7.5v5l3 1.8" /></> },
  // And the logbook beside it, by the same argument: a product nobody at
  // Phoniq can reach from their own session is one nobody can support.
  { to: '/loggbok', label: 'Loggbok', short: 'Loggbok', staffOnly: true,
    icon: <><path d="M5 3.6h14v16.8H5z" />
            <path d="M8.4 8h7.2M8.4 11.6h7.2" />
            <path d="M8.4 15.4l1.8 1.8 3.6-3.6" /></> },
  // A record of calibration certificates for Phoniq's sensors. It is the
  // kit page, and the kit is ours.
  { to: '/calibration', label: 'Hydrophones', short: 'Hydrophones', staffOnly: true,
    icon: <><path d="M4 18h16" /><path d="M7 18V9m5 9V5m5 13v-6" /></> },
  // Fleet-wide, and staff-only on the receiver. A two-site customer's version
  // of this number is not a smaller version of the same claim, it is a
  // different and misleading one. It returns per site as a product.
  { to: '/impact', label: 'Ocean impact', short: 'Impact', staffOnly: true,
    // A wave, because the subject is the sea rather than a chart of it.
    icon: <><path d="M2 8c2.5-2 4.5-2 7 0s4.5 2 7 0 4.5-2 6 0" />
            <path d="M2 14c2.5-2 4.5-2 7 0s4.5 2 7 0 4.5-2 6 0" />
            <path d="M2 20c2.5-2 4.5-2 7 0s4.5 2 7 0 4.5-2 6 0" /></> },
  // The audit trail and the attribution-conflict queue: both about how the
  // record is kept, neither about anybody's farm.
  { to: '/activity', label: 'Delivery', short: 'Delivery', staffOnly: true,
    icon: <path d="M3 12h4l3 7 4-14 3 7h4" /> },
]

/* What a CUSTOMER's menu holds.
 *
 * Every entry in NAV is staffOnly, so `navFor` used to return an empty list
 * and the rail rendered as nothing -- which left listening reachable only from
 * a text link at the bottom of the lice forecast page. To hear your hydrophone
 * you had to open a page about salmon lice and scroll past a four-week
 * forecast. Nobody finds that who has not been told.
 *
 * One entry per product, gated on the capability rather than on a guess, so a
 * farm that has not bought listening does not get a menu item that errors --
 * the argument the comment above NAV already makes about staff pages. Each
 * goes to a hop that lands on the farm when there is only one. */
const CUSTOMER_NAV: NavEntry[] = [
  { to: '/', label: 'Your farms', short: 'Farms', staffOnly: false,
    icon: <><path d="M12 21s7-5.3 7-11a7 7 0 1 0-14 0c0 5.7 7 11 7 11Z" />
            <circle cx="12" cy="10" r="2.5" /></>,
    can: () => true },
  // The mark's rings travel inward because a hydrophone only ever receives.
  { to: '/lytt', under: '/lytt', label: 'Listen', short: 'Listen', staffOnly: false,
    icon: <><circle cx="12" cy="12" r="2" />
            <path d="M8.5 8.5a5 5 0 0 0 0 7" />
            <path d="M15.5 8.5a5 5 0 0 1 0 7" />
            <path d="M5.5 5.5a9 9 0 0 0 0 13" />
            <path d="M18.5 5.5a9 9 0 0 1 0 13" /></>,
    can: (me: Me) => me.can?.listen !== false },
  /* The log of what was heard. Gated on the PURCHASE rather than on a
     capability: `can` is role verbs -- what kind of action this person may
     take -- and whether a farm bought a product is a different question. A
     menu item that cannot work is worse than a missing one. */
  { to: '/logg', under: '/logg', label: 'Log', short: 'Log', staffOnly: false,
    /* An hour of the day, marked. The log's unit is the hour, and the mark is
       the same red tick the timeline draws on one -- so the rail, the chart
       and the list all say "an hour stood out" in one language. */
    icon: <><circle cx="12" cy="12" r="9" />
            <path d="M12 7.5v5l3 1.8" /></>,
    /* Off the phone's tabbar, and only the phone's.
     *
     * `.tabbar` is `grid-auto-columns: 1fr` and renders every entry it is
     * given, so a customer with everything got seven columns of about fifty
     * pixels under an eleven-pixel label -- which renders, and is the point at
     * which a rail stops being scannable.
     *
     * Loggbok takes the slot because Loggbok is the daily job and the acoustic
     * log is a thing you go looking for. Nothing is retired: /logg keeps its
     * URL, its desktop entry and its link from the farm page. */
    railOnly: true,
    can: (me: Me) => me.features?.includes('analysis.log') ?? false },
  /* Loggboka. Its own purchase, for the same reason Laksvel has one: this is a
     compliance instrument kept by a person, not a claim derived from an
     audiogram, and serving it behind the acoustic log would sell one thing and
     deliver another. */
  { to: '/loggbok', under: '/loggbok', label: 'Loggbok', short: 'Loggbok', staffOnly: false,
    /* A ruled page with a tick: the round is a form, and the form gets done.
       Deliberately unlike the Log's clock face -- the two sit next to each
       other on the desktop rail and must not read as one thing twice. */
    icon: <><path d="M5 3.6h14v16.8H5z" />
            <path d="M8.4 8h7.2M8.4 11.6h7.2" />
            <path d="M8.4 15.4l1.8 1.8 3.6-3.6" /></>,
    can: (me: Me) => me.features?.includes('analysis.loggbok') ?? false },
  /* Fôring. Et DRIFTSprodukt, ikke en velferdsscore, og derfor sin egen
     nøkkel: `analysis.rest` selger hvilescoren, og å servere fôringen bak den
     ville solgt én ting og levert en annen. */
  { to: '/foring', under: '/foring', label: 'Feeding', short: 'Feeding', staffOnly: false,
    icon: <><path d="M4 19.5V9.2l8-4.7 8 4.7v10.3" />
            <path d="M9.2 19.5v-5.3h5.6v5.3" /></>,
    can: (me: Me) => me.features?.includes('analysis.feeding') ?? false },
  /* Appetitt. Sitt eget kjøp. Foreløpig bare maskoten, og derfor ikke i
     telefonens linje, som allerede er full. */
  { to: '/appetitt', under: '/appetitt', label: 'Appetite', short: 'Appetite', staffOnly: false,
    /* En pellet som faller mot en åpen munn. */
    icon: <><circle cx="12" cy="5.5" r="2" />
            <path d="M5 13.5c2 3.3 4.4 5 7 5s5-1.7 7-5" /></>,
    railOnly: true,
    can: (me: Me) => me.features?.includes('analysis.appetitt') ?? false },
  /* Laksvel. Sin egen nøkkel, av samme grunn som fôringen har det: den
     akustiske hvilescoren er en velferdspåstand bygget på et audiogram og på
     uttalte antakelser, og dette er en annen velferdspåstand med et annet
     grunnlag — en publisert protokoll, skåret av et menneske ved merdkanten.
     Å servere den ene bak den andre ville levert noe ingen har kjøpt. */
  { to: '/laksvel', under: '/laksvel', label: 'Laksvel', short: 'Laksvel', staffOnly: false,
    /* Et avkryssingsfelt: protokollen er en runde med et skjema. */
    icon: <><path d="M5 3.6h14v16.8H5z" />
            <path d="M8.4 9.2l2.2 2.2 4.6-4.6" />
            <path d="M8.4 15.6h7.2" /></>,
    can: (me: Me) => me.features?.includes('analysis.laksvel') ?? false },
  { to: '/lus', under: '', label: 'Lice', short: 'Lice', staffOnly: false,
    /* The same animal the forecast draws, in the rail's stroked language: a
       broad carapace, a tapered abdomen, and the two egg strings of an adult
       female. It was a generic bug -- an ellipse with six radiating legs --
       which named the wrong order of animal for a page about voksne hunnlus. */
    icon: <><ellipse cx="12" cy="7.1" rx="6.5" ry="4.4" />
            <path d="M9.6 10.9h4.8l-.5 3.2a2 2 0 0 1-3.8 0Z" />
            <path d="M11 14.2c-.5 3.3-.8 6-.9 8.1" />
            <path d="M13 14.2c.5 3.3.8 6 .9 8.1" /></>,
    can: (me: Me) => me.can?.lice_count !== false },
  /* Lusedagboka: the lice product's printed book. Off the phone's tabbar like
     Appetitt, because the bar is full and this is ordered a few times a year,
     not used every day. Strictly `=== true`, so a receiver that predates it
     hides the entry instead of offering a page that 404s. */
  { to: '/lusedagbok', under: '/lusedagbok', label: 'Lusedagbok', short: 'Lusedagbok',
    staffOnly: false,
    /* A notebook with its spiral along the top -- the binding is the thing
       that tells it apart from the Loggbok's ruled page beside it. */
    icon: <><path d="M5 6.5h14v14H5z" />
            <path d="M8 3.5v5M12 3.5v5M16 3.5v5" />
            <path d="M8.5 12.5h7M8.5 16h4.5" /></>,
    railOnly: true,
    can: (me: Me) => me.can?.lice_book === true },
]

/** `is_staff` is optional so this bundle still works against a receiver that
 *  predates it -- undefined means "no customers exist", so show everything. */
function navFor(me: Me) {
  if (me.is_staff !== false) return NAV
  return CUSTOMER_NAV.filter((n) => n.can?.(me) ?? true)
}

function Icon({ children }: { children: ReactNode }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {children}
    </svg>
  )
}

/** What to call this person's role, to this person.
 *
 *  Staff keep the user-level role: admin, operator, researcher, viewer are
 *  real distinctions on our own accounts and are what a staff member would say
 *  out loud. A customer's user-level role is `viewer` on every account and
 *  means nothing; theirs is the membership they were invited under. Where
 *  somebody belongs to more than one company the first is shown, which is the
 *  same one every other list on the page orders by. */
function roleWord(t: (k: string) => string, me: Me): string {
  if (me.is_staff !== false) return me.role
  /* A device holds a role's SCOPE; it is not that role. Its code decides which
     farms it reaches, and printing "Site manager" under the name of a tablet
     lying on a fôrflåte says something about a person that is not true of an
     object anyone walking past can pick up. */
  if (me.via === 'device') return t('Device')
  const member = me.orgs?.[0]?.member_role
  return member ? t(MEMBER_WORD[member] ?? member) : me.role
}

const MEMBER_WORD: Record<string, string> = {
  company_manager: 'Company manager',
  area_manager: 'Area manager',
  site_manager: 'Site manager',
  ship: 'Vessel',
  subsupplier: 'Subsupplier',
}

/** Ligger `path` på eller under `base`, på en SEGMENTgrense? */
function paa(path: string, base: string) {
  return base === '/' ? path === '/'
    : path === base || path.startsWith(base + '/')
}

const ANLEGGSDEL = /^\/sites\/[^/]+/

/** Hvilken post i menyen som eier siden som vises.
 *
 *  Den gamle enlinjeren bommet på to ting, og begge var synlige.
 *
 *  MENYEN PEKER PÅ HOPP. `/loggbok` sender deg videre til
 *  `/sites/{id}/loggbok` og er ute av adressefeltet før siden er tegnet -- så
 *  et rent `startsWith` på hoppet merket INGEN post på noen produktside, altså
 *  på hver eneste side en røkter faktisk jobber i. Menyen kunne markere seg
 *  selv nøyaktig i det halve sekundet ingen så på den. Derfor bærer hver post
 *  også halen den lander på, og begge sjekkes.
 *
 *  ET PREFIKS ER IKKE ET SEGMENT. `/logg` er et prefiks av `/loggbok`, så
 *  `startsWith` lyste opp den akustiske loggen og loggboka samtidig. To merkede
 *  poster er verre enn ingen: den ene av dem lyver. */
function isActive(n: NavEntry, path: string) {
  if (paa(path, n.to)) return true
  if (n.under === undefined) return false
  const rest = path.replace(ANLEGGSDEL, '')
  return rest !== path && paa(rest || '/', n.under || '/')
}

export interface Crumb {
  label: string
  to?: string
}

export function Shell({
  me, crumbs, children, onSignedOut,
}: {
  me: Me
  crumbs: Crumb[]
  children: ReactNode
  onSignedOut: () => void
}) {
  const path = usePath()
  const t = useT()

  const signOut = async () => {
    try {
      await api.post('/v1/auth/logout')
    } finally {
      onSignedOut()
    }
  }

  return (
    <div className="shell">
      <a className="skip" href="#main">Skip to content</a>

      {/* On every screen, not just the one it was started from. A staff member
          who forgets they are in a preview reports the missing controls as a
          bug -- and the controls are missing because a preview is read-only,
          which is a sentence that has to be on screen rather than in a
          changelog. */}
      {me.viewing_as && (
        <div className="viewing-as">
          <span>
            {t('Viewing as')} <strong>{me.viewing_as.org_name}</strong>
            {' — '}{t('read only. Your own farms and menus are hidden until you leave.')}
          </span>
          <button className="btn sm" onClick={() => void stopViewAs()
            .finally(() => window.location.assign('/'))}>
            {t('Back to Phoniq')}
          </button>
        </div>
      )}

      {/* Desktop only. On a phone this is the tab bar at the bottom, because
          the top of a phone is not where a thumb reaches. */}
      <nav className="rail" aria-label="Main">
        <Link to="/" className="wordmark">
          <span className="dot" aria-hidden="true" />
          Phoniq
        </Link>

        {/* A customer has one destination -- their farms -- and the wordmark
            already goes there. A navigation list of one item is worse than
            none, so the chrome is absent rather than nearly empty. */}
        {navFor(me).length > 0 && (
          <div className="nav">
            {navFor(me).map((n) => (
              <Link key={n.to} to={n.to}
                aria-current={isActive(n, path) ? 'page' : undefined}>
                <Icon>{n.icon}</Icon>
                {t(n.label)}
              </Link>
            ))}
          </div>
        )}

        <div className="rail-foot">
          <div style={{ padding: '0 8px 12px' }}>
            <div style={{ fontWeight: 600 }}>
              {me.display_name || me.email || me.label}
            </div>
            {/* --muted, not --faint. This is the one word telling you what you
                are allowed to do, and it used to be the hardest word on the
                page to read at 2.96:1.

                It was also the wrong word. `me.role` is the USER-level role,
                which is `viewer` on every customer account -- the row exists
                so staff can be admin or researcher, and a customer's answer to
                it is a default nobody chose. What a customer was actually
                invited as lives on the membership: company manager, area
                manager, site manager. Somebody invited as a company manager
                read "viewer" under their own name, on the one line that says
                what they may do. */}
            <div className="muted" style={{ fontSize: 14 }}>{roleWord(t, me)}</div>
          </div>
          {/* Not in NAV, deliberately. NAV drives the mobile tabbar too, and a
              reference document read once a quarter does not belong beside the
              five things people came here to do. The account page joins it for
              the same reason: you change your password roughly never. */}
          {/* Staff only, and in the foot rather than in NAV: NAV drives the
              mobile tab bar, whose five slots are the five things people came
              here to do. Onboarding a company is done once per customer, at a
              desk. */}
          {/* Shown while previewing too. is_staff is false during a preview --
              that is the mechanism, and it correctly hides staff navigation --
              but it also removed every route back, leaving the banner as the
              only exit. One missed banner and an admin is stuck inside a
              customer's portal with no visible way out. */}
          {(me.is_staff !== false || me.viewing_as) && (
            <Link to="/operators" className="rail-doc"
              aria-current={path.startsWith('/operators') ? 'page' : undefined}>
              {t('Operators')}
            </Link>
          )}
          {/* In the foot rather than in NAV, for the reason stated above it:
              NAV drives the mobile tab bar, whose five slots are the five
              things people came here to do. Connecting the crew's phones is
              done at a desk, once, and then not again for a season.

              Capability rather than staff-or-not, because this is a CUSTOMER's
              screen: the company manager hands out the codes, and nobody at
              Phoniq does it for them. */}
          {me.can?.manage_people && (
            <Link to="/enheter" className="rail-doc"
              aria-current={path === '/enheter' ? 'page' : undefined}>
              {t('Devices')}
            </Link>
          )}
          {/* SIGNING OUT IS NOT OFFERED TO A DEVICE, and this is the single
              highest-consequence line on the page now that a device reaches
              the portal at all. /v1/auth/logout is plane-neutral -- it revokes
              by raw token and will happily end a device session -- so one tap
              by somebody tidying up strands the tablet on the enrollment
              screen, needing a code that nobody standing on the walkway has.

              Not hidden, though: a tablet being handed on or sold still needs
              a way out. It lives on /enhet, next to its own copy explaining
              that the next enrollment is a NEW device with no history, which
              is the sentence that belongs beside a destructive verb and does
              not fit on a button in a rail. */}
          {me.via === 'device' ? (
            <Link to="/enhet" className="rail-doc"
              aria-current={path === '/enhet' ? 'page' : undefined}>
              {t('This device')}
            </Link>
          ) : (
            <Link to="/account" className="rail-doc"
              aria-current={path === '/account' ? 'page' : undefined}>
              {t('Your account')}
            </Link>
          )}
          <Link to="/security" className="rail-doc"
            aria-current={path === '/security' ? 'page' : undefined}>
            {t('Security')}
          </Link>
          <LangSwitch />
          {me.via !== 'device' && (
            <button className="btn ghost sm" onClick={() => void signOut()}>
              {t('Sign out')}
            </button>
          )}
        </div>
      </nav>

      <div className="main">
        <header className="topbar">
          <Link to="/" className="topbar-mark">
            <span className="dot" aria-hidden="true" />
            Phoniq
          </Link>

          <div className="crumbs">
            {crumbs.map((c, i) => (
              <span key={i} style={{ display: 'contents' }}>
                {i > 0 && <span className="sep" aria-hidden="true">/</span>}
                {c.to && i < crumbs.length - 1 ? (
                  <Link to={c.to}>{c.label}</Link>
                ) : (
                  <span className="here">{c.label}</span>
                )}
              </span>
            ))}
          </div>

          {/* Same reason as the rail foot: a device gets its own screen here,
              never a control that ends its session. */}
          {me.via === 'device' ? (
            <Link to="/enhet" className="btn ghost sm topbar-account"
              title={me.display_name || me.label}>
              {t('This device')}
            </Link>
          ) : (
            <button className="btn ghost sm topbar-account"
              onClick={() => void signOut()}
              title={`${me.display_name || me.email || me.label} (${me.role})`}>
              Sign out
            </button>
          )}
        </header>

        <main id="main" className="page">
          {children}
        </main>
      </div>

      {/* The phone's tabbar, which is the same list minus whatever has said it
          belongs on the desktop rail only. One flag rather than a second array:
          two lists of destinations disagree the first time somebody adds to one
          of them. */}
      {navFor(me).length > 0 && (
        <nav className="tabbar" aria-label="Main">
          {navFor(me).filter((n) => !n.railOnly).map((n) => (
            <Link key={n.to} to={n.to}
              aria-current={isActive(n, path) ? 'page' : undefined}>
              <Icon>{n.icon}</Icon>
              {t(n.short)}
            </Link>
          ))}
        </nav>
      )}
    </div>
  )
}

/** The one primary action for a page, always in the same place: bottom of the
 *  head block on a phone, top right on a desktop. */
export function PageHead({
  title, lead, cta, tight,
}: { title: string; lead?: ReactNode; cta?: ReactNode; tight?: boolean }) {
  return (
    /* `tight` puts the lead on the title's baseline instead of under it, and
     * shrinks both. For pages whose point is BELOW the fold -- the player is
     * the one -- a full-height masthead costs about ninety pixels, and ninety
     * pixels is the difference between the filters being on screen and being
     * a scroll away. The name still leads; it just stops being a poster. */
    <div className={`page-head${tight ? ' tight' : ''}`}>
      <div className="grow">
        <h1 className="display">{title}</h1>
        {lead && (
          <p className="lead" style={tight ? undefined
            : { marginTop: 8, marginBottom: 0 }}>{lead}</p>
        )}
      </div>
      {cta && <div className="cta">{cta}</div>}
    </div>
  )
}
